import { BaseFeature } from '../../core/base-feature.js';

/**
 * InterrogationFeature - Frontend feature for managing interrogation sessions
 * Handles: starting sessions, monitoring typing, controlling outcomes, viewing history
 */
export class InterrogationFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.isSessionActive = false;
        this.sessionState = null;
        this.questions = [];
        this.sessions = [];
        this.answers = [];
        this.categories = [];

        // Live typing state
        this.liveTyping = null;

        // Event callbacks for React components
        this.typingCallbacks = new Set();
        this.sessionCallbacks = new Set();
        this.hesitationCallbacks = new Set();
        this.answerCallbacks = new Set();
        this.submitAttemptCallbacks = new Set();
        this.updateCallbacks = new Set();
        this.autoAcceptTimerCallbacks = new Set();
        this.pressureTimerCallbacks = new Set();
        this.contradictionCallbacks = new Set();

        // Last contradiction data
        this._lastContradiction = null;

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'interrogation',
            name: 'Interrogation',
            version: 'v1.0',
            capabilities: [
                'start_session',
                'end_session',
                'control_answer',
                'manage_questions',
                'view_history',
                'compare_answers'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Interrogation] Initializing...');
        console.log('[Interrogation] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId, fromId } = message;
        const command = cmd || action;
        this._validateInbound(command, payload); // contract check (dev/test only)

        switch (command) {
            // Real-time updates
            case 'typing_update':
                return this.handleTypingUpdate(payload);

            case 'hesitation':
                return this.handleHesitation(payload);

            case 'answer_submitted':
                return this.handleAnswerSubmitted(payload);

            case 'submit_attempt':
                return this.handleSubmitAttempt(payload);

            case 'session_ended':
                return this.handleSessionEnded(payload);

            case 'contradiction_detected':
                return this.handleContradictionDetected(payload);

            // Responses to commands
            case 'start_session_response':
                return this.handleStartSessionResponse(payload);

            case 'end_session_response':
                return this.handleEndSessionResponse(payload);

            case 'accept_response':
            case 'reject_response':
            case 'flag_response':
                return this.handleControlResponse(payload);

            case 'follow_up_response':
                return this.handleFollowUpResponse(payload);

            case 'get_questions_response':
                return this.handleGetQuestionsResponse(payload);

            case 'add_question_response':
                return this.handleAddQuestionResponse(payload);

            case 'update_question_response':
                return this.handleUpdateQuestionResponse(payload);

            case 'delete_question_response':
                return this.handleDeleteQuestionResponse(payload);

            case 'reset_questions_response':
                return this.handleResetQuestionsResponse(payload);

            case 'get_db_stats_response':
                return this.handleGetDbStatsResponse(payload);

            case 'get_sessions_response':
                return this.handleGetSessionsResponse(payload);

            case 'get_answers_response':
                return this.handleGetAnswersResponse(payload);

            case 'get_session_state_response':
                return this.handleGetSessionStateResponse(payload);

            case 'compare_answers_response':
                return this.handleCompareAnswersResponse(payload);

            case 'expose_answers_response':
                return this.handleExposeAnswersResponse(payload);

            case 'flag_as_lie_response':
                return this.handleFlagAsLieResponse(payload);

            case 'configure_ai_analysis_response':
                return this.handleConfigureAiAnalysisResponse(payload);

            case 'get_ai_analysis_config_response':
                return this.handleGetAiAnalysisConfigResponse(payload);

            case 'analyze_answer_response':
                return this.handleAnalyzeAnswerResponse(payload);

            case 'generate_questions_response':
                return this.handleGenerateQuestionsResponse(payload);

            case 'check_resume_response':
                return this.handleCheckResumeResponse(payload);

            case 'resume_session_response':
                return this.handleResumeSessionResponse(payload);

            case 'get_analytics_response':
                return this.handleGetAnalyticsResponse(payload);

            case 'get_flagged_data_response':
                return this.handleGetFlaggedDataResponse(payload);

            case 'get_schedules_response':
                return this.handleGetSchedulesResponse(payload);

            case 'create_schedule_response':
                return this.handleCreateScheduleResponse(payload);

            case 'update_schedule_response':
                return this.handleUpdateScheduleResponse(payload);

            case 'delete_schedule_response':
                return this.handleDeleteScheduleResponse(payload);

            case 'get_settings_response':
                return this.handleGetSettingsResponse(payload);

            case 'save_settings_response':
                return this.handleSaveSettingsResponse(payload);

            case 'initial_state':
                return this.handleInitialState(payload);

            case 'auto_accept_timer':
                return this.handleAutoAcceptTimer(payload);

            case 'pressure_timer':
                return this.handlePressureTimer(payload);

            default:
                console.warn(`[Interrogation] Unknown command: ${command}`);
        }
    }

    // =====================================
    // Real-time Event Handlers
    // =====================================

    handleTypingUpdate(payload) {
        this.liveTyping = payload;
        this.notifyTypingListeners(payload);
    }

    handleHesitation(payload) {
        console.log(`[Interrogation] Hesitation detected: ${payload.hesitationNumber}`);
        this.notifyHesitationListeners(payload);
    }

    handleAnswerSubmitted(payload) {
        console.log(`[Interrogation] Answer submitted: ${payload.answerId}`);
        this.liveTyping = null;
        this.notifyAnswerListeners(payload);
    }

    handleSubmitAttempt(payload) {
        console.log(`[Interrogation] Submit attempt failed: ${payload.charCount}/${payload.minRequired} chars`);
        this.notifySubmitAttemptListeners(payload);
    }

    handleSessionEnded(payload) {
        console.log(`[Interrogation] Session ended: ${payload.reason}`);
        this.isSessionActive = false;
        this.sessionState = null;
        this.liveTyping = null;
        this._lastContradiction = null;
        this.notifySessionListeners({ active: false, ...payload });
    }

    handleContradictionDetected(payload) {
        console.log(`[Interrogation] Contradiction detected: ${payload.contradictions?.length} contradictions`);
        this._lastContradiction = payload;
        this.notifyContradictionListeners(payload);
    }

    handleInitialState(payload) {
        console.log('[Interrogation] Received initial state');
        this.isSessionActive = payload.active || false;
        this.sessionState = payload.sessionState || null;
        this.notifySessionListeners({ active: this.isSessionActive, state: this.sessionState });
    }

    handleAutoAcceptTimer(payload) {
        // Desktop sends: { action: 'start'|'tick'|'paused'|'resumed'|'cancelled'|'accepted', delay, timeLeft }
        console.log(`[Interrogation] Auto-accept timer: ${payload.action}`, payload);
        this.notifyAutoAcceptTimerListeners(payload);
    }

    handlePressureTimer(payload) {
        // Desktop sends: { action: 'start'|'tick'|'lockout'|'lockout_tick'|'lockout_end'|'reset'|'stop', timeLeft, duration, lockoutDuration }
        console.log(`[Interrogation] Pressure timer: ${payload.action}`, payload);
        this.notifyPressureTimerListeners(payload);
    }

    // =====================================
    // Session Control
    // =====================================

    /**
     * Start an interrogation session
     * @param {Array<number>} questionIds - IDs of questions to ask
     * @param {Object} settings - Session settings (oppressive modes)
     * @returns {Promise<Object>} Result
     */
    async startSession(questionIds, settings = {}) {
        console.log(`[Interrogation] Starting session with ${questionIds.length} questions`, settings);

        return new Promise((resolve) => {
            this.sendMessage('start_session', { questionIds, settings });
            this._pendingRequests.set('start_session', resolve);
        });
    }

    handleStartSessionResponse(payload) {
        const resolve = this._pendingRequests.get('start_session');
        if (resolve) {
            this._pendingRequests.delete('start_session');
            resolve(payload);
        }

        if (payload.success) {
            this.isSessionActive = true;
            this.sessionState = {
                sessionId: payload.sessionId,
                questionCount: payload.questionCount
            };
            console.log(`[Interrogation] Session started: ${payload.sessionId}`);
            this.notifySessionListeners({ active: true, state: this.sessionState });
        } else {
            console.error(`[Interrogation] Failed to start session: ${payload.error}`);
        }
    }

    /**
     * End the current session
     * @returns {Promise<Object>} Result
     */
    async endSession() {
        console.log('[Interrogation] Ending session');

        return new Promise((resolve) => {
            this.sendMessage('end_session', {});
            this._pendingRequests.set('end_session', resolve);
        });
    }

    handleEndSessionResponse(payload) {
        const resolve = this._pendingRequests.get('end_session');
        if (resolve) {
            this._pendingRequests.delete('end_session');
            resolve(payload);
        }
    }

    /**
     * Accept the current answer
     * @returns {Promise<Object>} Result
     */
    async acceptAnswer() {
        console.log('[Interrogation] Accepting answer');
        return new Promise((resolve) => {
            this.sendMessage('accept', {});
            this._pendingRequests.set('accept', resolve);
        });
    }

    /**
     * Reject the current answer
     * @param {Object} options - Options
     * @param {boolean} options.askNewQuestion - Whether to ask the same question again
     * @param {string} options.message - Message to display
     * @returns {Promise<Object>} Result
     */
    async rejectAnswer(options = {}) {
        console.log('[Interrogation] Rejecting answer');
        return new Promise((resolve) => {
            this.sendMessage('reject', options);
            this._pendingRequests.set('reject', resolve);
        });
    }

    /**
     * Flag the current answer
     * @param {string} reason - Reason for flagging
     * @returns {Promise<Object>} Result
     */
    async flagAnswer(reason = null) {
        console.log('[Interrogation] Flagging answer');
        return new Promise((resolve) => {
            this.sendMessage('flag', { reason });
            this._pendingRequests.set('flag', resolve);
        });
    }

    handleControlResponse(payload) {
        // Generic handler for accept/reject/flag responses
        for (const key of ['accept', 'reject', 'flag']) {
            const resolve = this._pendingRequests.get(key);
            if (resolve) {
                this._pendingRequests.delete(key);
                resolve(payload);
                break;
            }
        }
    }

    /**
     * Demand more detail from the user
     * @param {string} message - Message to display
     * @returns {Promise<Object>} Result
     */
    async demandMore(message = 'More detail required') {
        console.log(`[Interrogation] Demanding more: ${message}`);
        this.sendMessage('demand_more', { message });
        return { success: true };
    }

    /**
     * Change the minimum character requirement
     * @param {number} newMin - New minimum
     * @returns {Promise<Object>} Result
     */
    async setMinimum(newMin) {
        console.log(`[Interrogation] Setting minimum to ${newMin}`);
        this.sendMessage('set_minimum', { newMin });
        return { success: true };
    }

    /**
     * Approve current answer and ask a follow-up question
     * @param {string} questionText - The follow-up question to ask
     * @param {number} minChars - Minimum characters required for the follow-up answer
     * @returns {Promise<Object>} Result
     */
    async followUp(questionText, minChars = 100) {
        console.log(`[Interrogation] Sending follow-up: ${questionText}`);
        return new Promise((resolve) => {
            this.sendMessage('follow_up', { questionText, minChars });
            this._pendingRequests.set('follow_up', resolve);
        });
    }

    handleFollowUpResponse(payload) {
        const resolve = this._pendingRequests.get('follow_up');
        if (resolve) {
            this._pendingRequests.delete('follow_up');
            resolve(payload);
        }
    }

    /**
     * Get current session state
     * @returns {Promise<Object>} Session state
     */
    async getSessionState() {
        return new Promise((resolve) => {
            this.sendMessage('get_session_state', {});
            this._pendingRequests.set('get_session_state', resolve);
        });
    }

    handleGetSessionStateResponse(payload) {
        const resolve = this._pendingRequests.get('get_session_state');
        if (resolve) {
            this._pendingRequests.delete('get_session_state');
            resolve(payload);
        }

        if (payload.success) {
            this.isSessionActive = payload.active;
            this.sessionState = payload.state;
            this.notifySessionListeners({ active: payload.active, state: payload.state });
        }
    }

    // =====================================
    // Question Bank
    // =====================================

    /**
     * Get questions from question bank
     * @param {Object} options - Query options
     * @returns {Promise<Object>} Questions list
     */
    async getQuestions(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('get_questions', options);
            this._pendingRequests.set('get_questions', resolve);
        });
    }

    handleGetQuestionsResponse(payload) {
        const resolve = this._pendingRequests.get('get_questions');
        if (resolve) {
            this._pendingRequests.delete('get_questions');
            resolve(payload);
        }

        if (payload.success) {
            this.questions = payload.questions || [];
            this.categories = payload.categories || [];
            this.notifyUpdateListeners();
        }
    }

    /**
     * Add a new question
     * @param {Object} question - Question data
     * @returns {Promise<Object>} Result
     */
    async addQuestion(question) {
        return new Promise((resolve) => {
            this.sendMessage('add_question', question);
            this._pendingRequests.set('add_question', resolve);
        });
    }

    handleAddQuestionResponse(payload) {
        const resolve = this._pendingRequests.get('add_question');
        if (resolve) {
            this._pendingRequests.delete('add_question');
            resolve(payload);
        }

        if (payload.success) {
            console.log(`[Interrogation] Question added: ${payload.id}`);
            this.getQuestions(); // Refresh list
        }
    }

    /**
     * Update a question
     * @param {number} id - Question ID
     * @param {Object} updates - Updates to apply
     * @returns {Promise<Object>} Result
     */
    async updateQuestion(id, updates) {
        return new Promise((resolve) => {
            this.sendMessage('update_question', { id, ...updates });
            this._pendingRequests.set('update_question', resolve);
        });
    }

    handleUpdateQuestionResponse(payload) {
        const resolve = this._pendingRequests.get('update_question');
        if (resolve) {
            this._pendingRequests.delete('update_question');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[Interrogation] Question updated');
            this.getQuestions(); // Refresh list
        }
    }

    /**
     * Delete a question
     * @param {number} id - Question ID
     * @returns {Promise<Object>} Result
     */
    async deleteQuestion(id) {
        return new Promise((resolve) => {
            this.sendMessage('delete_question', { id });
            this._pendingRequests.set('delete_question', resolve);
        });
    }

    handleDeleteQuestionResponse(payload) {
        const resolve = this._pendingRequests.get('delete_question');
        if (resolve) {
            this._pendingRequests.delete('delete_question');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[Interrogation] Question deleted');
            this.getQuestions(); // Refresh list
        }
    }

    /**
     * Reset all questions - clears and re-seeds the question bank
     * Use this if questions are missing or corrupted
     * @returns {Promise<Object>} Result with new question count
     */
    async resetQuestions() {
        console.log('[Interrogation] Resetting all questions...');
        return new Promise((resolve) => {
            this.sendMessage('reset_questions', {});
            this._pendingRequests.set('reset_questions', resolve);
        });
    }

    handleResetQuestionsResponse(payload) {
        const resolve = this._pendingRequests.get('reset_questions');
        if (resolve) {
            this._pendingRequests.delete('reset_questions');
            resolve(payload);
        }

        if (payload.success) {
            console.log(`[Interrogation] Questions reset, new count: ${payload.count}`);
            this.getQuestions(); // Refresh list
        }
    }

    /**
     * Get database statistics
     * @returns {Promise<Object>} Database stats including question/session/answer counts
     */
    async getDbStats() {
        return new Promise((resolve) => {
            this.sendMessage('get_db_stats', {});
            this._pendingRequests.set('get_db_stats', resolve);
        });
    }

    handleGetDbStatsResponse(payload) {
        const resolve = this._pendingRequests.get('get_db_stats');
        if (resolve) {
            this._pendingRequests.delete('get_db_stats');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[Interrogation] Database stats:', payload.stats);
        }
    }

    // =====================================
    // History
    // =====================================

    /**
     * Get session history
     * @param {Object} options - Query options
     * @returns {Promise<Object>} Sessions list
     */
    async getSessions(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('get_sessions', options);
            this._pendingRequests.set('get_sessions', resolve);
        });
    }

    handleGetSessionsResponse(payload) {
        const resolve = this._pendingRequests.get('get_sessions');
        if (resolve) {
            this._pendingRequests.delete('get_sessions');
            resolve(payload);
        }

        if (payload.success) {
            this.sessions = payload.sessions || [];
            this.notifyUpdateListeners();
        }
    }

    /**
     * Get answer history
     * @param {Object} options - Query options (sessionId, questionId)
     * @returns {Promise<Object>} Answers list
     */
    async getAnswers(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('get_answers', options);
            this._pendingRequests.set('get_answers', resolve);
        });
    }

    handleGetAnswersResponse(payload) {
        const resolve = this._pendingRequests.get('get_answers');
        if (resolve) {
            this._pendingRequests.delete('get_answers');
            resolve(payload);
        }

        if (payload.success) {
            this.answers = payload.answers || [];
            this.notifyUpdateListeners();
        }
    }

    /**
     * Compare answers for a question
     * @param {number} questionId - Question ID
     * @returns {Promise<Object>} Comparison data
     */
    async compareAnswers(questionId) {
        return new Promise((resolve) => {
            this.sendMessage('compare_answers', { questionId });
            this._pendingRequests.set('compare_answers', resolve);
        });
    }

    handleCompareAnswersResponse(payload) {
        const resolve = this._pendingRequests.get('compare_answers');
        if (resolve) {
            this._pendingRequests.delete('compare_answers');
            resolve(payload);
        }
    }

    /**
     * Generate an AI "exposure" across every answer to a question — a summary
     * that calls out lies, concealment, and how the story deviated (deleted text
     * included as concealment evidence).
     * @param {number} questionId
     * @returns {Promise<Object>} { success, exposure, questionText, answerCount }
     */
    async exposeQuestion(questionId) {
        return new Promise((resolve) => {
            this.sendMessage('expose_answers', { questionId });
            this._pendingRequests.set('expose_answers', resolve);
        });
    }

    handleExposeAnswersResponse(payload) {
        const resolve = this._pendingRequests.get('expose_answers');
        if (resolve) {
            this._pendingRequests.delete('expose_answers');
            resolve(payload);
        }
    }

    /**
     * Flag answers as a lie
     * @param {number} answerId1 - First answer ID
     * @param {number} answerId2 - Second answer ID
     * @param {string} aiAnalysis - Optional AI analysis
     * @returns {Promise<Object>} Result
     */
    async flagAsLie(answerId1, answerId2, aiAnalysis = null) {
        return new Promise((resolve) => {
            this.sendMessage('flag_as_lie', { answerId1, answerId2, aiAnalysis });
            this._pendingRequests.set('flag_as_lie', resolve);
        });
    }

    handleFlagAsLieResponse(payload) {
        const resolve = this._pendingRequests.get('flag_as_lie');
        if (resolve) {
            this._pendingRequests.delete('flag_as_lie');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[Interrogation] Answers flagged as lie');
        }
    }

    // =====================================
    // AI Analysis
    // =====================================

    /**
     * Configure AI analysis API key
     * @param {string} apiKey - Anthropic API key
     * @param {string} model - Optional model override
     * @returns {Promise<Object>} Result
     */
    async configureAiAnalysis(apiKey, model = null) {
        return new Promise((resolve) => {
            this.sendMessage('configure_ai_analysis', { apiKey, model });
            this._pendingRequests.set('configure_ai_analysis', resolve);
        });
    }

    handleConfigureAiAnalysisResponse(payload) {
        const resolve = this._pendingRequests.get('configure_ai_analysis');
        if (resolve) {
            this._pendingRequests.delete('configure_ai_analysis');
            resolve(payload);
        }
    }

    /**
     * Get AI analysis configuration status
     * @returns {Promise<Object>} Config status { isConfigured, model }
     */
    async getAiAnalysisConfig() {
        return new Promise((resolve) => {
            this.sendMessage('get_ai_analysis_config', {});
            this._pendingRequests.set('get_ai_analysis_config', resolve);
        });
    }

    handleGetAiAnalysisConfigResponse(payload) {
        const resolve = this._pendingRequests.get('get_ai_analysis_config');
        if (resolve) {
            this._pendingRequests.delete('get_ai_analysis_config');
            resolve(payload);
        }
    }

    /**
     * Analyze an answer using AI
     * @param {Object} params - Analysis parameters
     * @param {number} params.answerId - Optional answer ID to store analysis
     * @param {string} params.questionText - The question that was asked
     * @param {string} params.answerText - The answer to analyze
     * @param {number} params.minCharCount - Minimum character requirement
     * @param {string} params.category - Question category
     * @returns {Promise<Object>} Analysis result { success, analysis: { score, relevance, detailLevel, avoidanceDetected, suggestedFollowups, briefAnalysis } }
     */
    async analyzeAnswer(params) {
        return new Promise((resolve) => {
            this.sendMessage('analyze_answer', params);
            this._pendingRequests.set('analyze_answer', resolve);
        });
    }

    handleAnalyzeAnswerResponse(payload) {
        const resolve = this._pendingRequests.get('analyze_answer');
        if (resolve) {
            this._pendingRequests.delete('analyze_answer');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[Interrogation] Answer analyzed:', payload.analysis);
        }
    }

    // =====================================
    // Session Resume
    // =====================================

    /**
     * Check for a resumable session
     * @returns {Promise<Object>} { hasResumable, state }
     */
    async checkResume() {
        return new Promise((resolve) => {
            this.sendMessage('check_resume', {});
            this._pendingRequests.set('check_resume', resolve);
        });
    }

    handleCheckResumeResponse(payload) {
        const resolve = this._pendingRequests.get('check_resume');
        if (resolve) {
            this._pendingRequests.delete('check_resume');
            resolve(payload);
        }
    }

    /**
     * Resume an interrupted session
     * @returns {Promise<Object>} Result
     */
    async resumeSession() {
        return new Promise((resolve) => {
            this.sendMessage('resume_session', {});
            this._pendingRequests.set('resume_session', resolve);
        });
    }

    handleResumeSessionResponse(payload) {
        const resolve = this._pendingRequests.get('resume_session');
        if (resolve) {
            this._pendingRequests.delete('resume_session');
            resolve(payload);
        }

        if (payload.success) {
            this.isSessionActive = true;
            this.sessionState = { sessionId: payload.sessionId };
            console.log(`[Interrogation] Session resumed: ${payload.sessionId}`);
            this.notifySessionListeners({ active: true, state: this.sessionState });
        }
    }

    /**
     * Generate questions using AI
     * @param {string} topic - Topic for question generation
     * @param {number} count - Number of questions to generate (default 5)
     * @param {boolean} useDossier - Whether to include dossier context
     * @returns {Promise<Object>} Result with generated questions
     */
    async generateQuestions(topic, count = 5, useDossier = false) {
        return new Promise((resolve) => {
            this.sendMessage('generate_questions', { topic, count, useDossier });
            this._pendingRequests.set('generate_questions', resolve);
        });
    }

    handleGenerateQuestionsResponse(payload) {
        const resolve = this._pendingRequests.get('generate_questions');
        if (resolve) {
            this._pendingRequests.delete('generate_questions');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[Interrogation] Questions generated:', payload.questions?.length);
        }
    }

    async getAnalytics() {
        return new Promise((resolve) => {
            this.sendMessage('get_analytics', {});
            this._pendingRequests.set('get_analytics', resolve);
        });
    }

    handleGetAnalyticsResponse(payload) {
        const resolve = this._pendingRequests.get('get_analytics');
        if (resolve) {
            this._pendingRequests.delete('get_analytics');
            resolve(payload);
        }
    }

    async getFlaggedData() {
        return new Promise((resolve) => {
            this.sendMessage('get_flagged_data', {});
            this._pendingRequests.set('get_flagged_data', resolve);
        });
    }

    handleGetFlaggedDataResponse(payload) {
        const resolve = this._pendingRequests.get('get_flagged_data');
        if (resolve) {
            this._pendingRequests.delete('get_flagged_data');
            resolve(payload);
        }
    }

    // =====================================
    // Schedules
    // =====================================

    async getSchedules() {
        return new Promise((resolve) => {
            this.sendMessage('get_schedules', {});
            this._pendingRequests.set('get_schedules', resolve);
        });
    }

    handleGetSchedulesResponse(payload) {
        const resolve = this._pendingRequests.get('get_schedules');
        if (resolve) {
            this._pendingRequests.delete('get_schedules');
            resolve(payload);
        }
    }

    async createSchedule(data) {
        return new Promise((resolve) => {
            this.sendMessage('create_schedule', data);
            this._pendingRequests.set('create_schedule', resolve);
        });
    }

    handleCreateScheduleResponse(payload) {
        const resolve = this._pendingRequests.get('create_schedule');
        if (resolve) {
            this._pendingRequests.delete('create_schedule');
            resolve(payload);
        }
    }

    async updateSchedule(id, data) {
        return new Promise((resolve) => {
            this.sendMessage('update_schedule', { id, ...data });
            this._pendingRequests.set('update_schedule', resolve);
        });
    }

    handleUpdateScheduleResponse(payload) {
        const resolve = this._pendingRequests.get('update_schedule');
        if (resolve) {
            this._pendingRequests.delete('update_schedule');
            resolve(payload);
        }
    }

    async deleteSchedule(id) {
        return new Promise((resolve) => {
            this.sendMessage('delete_schedule', { id });
            this._pendingRequests.set('delete_schedule', resolve);
        });
    }

    handleDeleteScheduleResponse(payload) {
        const resolve = this._pendingRequests.get('delete_schedule');
        if (resolve) {
            this._pendingRequests.delete('delete_schedule');
            resolve(payload);
        }
    }

    /**
     * Load persisted controller settings from the desktop.
     * @returns {Promise<Object>} { success, settings } — settings is null if never saved.
     */
    async getSettings() {
        return new Promise((resolve) => {
            this.sendMessage('get_settings', {});
            this._pendingRequests.set('get_settings', resolve);
        });
    }

    handleGetSettingsResponse(payload) {
        const resolve = this._pendingRequests.get('get_settings');
        if (resolve) {
            this._pendingRequests.delete('get_settings');
            resolve(payload);
        }
    }

    /**
     * Persist the controller settings object on the desktop.
     * @param {Object} settings - The full session-settings object.
     * @returns {Promise<Object>} { success }
     */
    async saveSettings(settings) {
        return new Promise((resolve) => {
            this.sendMessage('save_settings', { settings });
            this._pendingRequests.set('save_settings', resolve);
        });
    }

    handleSaveSettingsResponse(payload) {
        const resolve = this._pendingRequests.get('save_settings');
        if (resolve) {
            this._pendingRequests.delete('save_settings');
            resolve(payload);
        }
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    /**
     * Subscribe to typing updates
     * @param {Function} callback - Called with typing state
     * @returns {Function} Unsubscribe function
     */
    onTypingUpdate(callback) {
        this.typingCallbacks.add(callback);
        return () => this.typingCallbacks.delete(callback);
    }

    /**
     * Subscribe to session changes
     * @param {Function} callback - Called with session state
     * @returns {Function} Unsubscribe function
     */
    onSessionChange(callback) {
        this.sessionCallbacks.add(callback);
        return () => this.sessionCallbacks.delete(callback);
    }

    /**
     * Subscribe to hesitation events
     * @param {Function} callback - Called with hesitation data
     * @returns {Function} Unsubscribe function
     */
    onHesitation(callback) {
        this.hesitationCallbacks.add(callback);
        return () => this.hesitationCallbacks.delete(callback);
    }

    /**
     * Subscribe to answer submissions
     * @param {Function} callback - Called with answer data
     * @returns {Function} Unsubscribe function
     */
    onAnswerSubmitted(callback) {
        this.answerCallbacks.add(callback);
        return () => this.answerCallbacks.delete(callback);
    }

    /**
     * Subscribe to submit attempts (failed submissions due to insufficient characters)
     * @param {Function} callback - Called when user tries to submit with insufficient chars
     * @returns {Function} Unsubscribe function
     */
    onSubmitAttempt(callback) {
        this.submitAttemptCallbacks.add(callback);
        return () => this.submitAttemptCallbacks.delete(callback);
    }

    /**
     * Subscribe to general updates (questions, sessions, answers)
     * @param {Function} callback - Called when data changes
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    /**
     * Subscribe to auto-accept timer events
     * @param {Function} callback - Called with timer state { action, delay, timeLeft }
     * @returns {Function} Unsubscribe function
     */
    onAutoAcceptTimer(callback) {
        this.autoAcceptTimerCallbacks.add(callback);
        return () => this.autoAcceptTimerCallbacks.delete(callback);
    }

    /**
     * Subscribe to pressure timer updates
     * @param {Function} callback - Called with timer state { action, timeLeft, duration, lockoutDuration }
     * @returns {Function} Unsubscribe function
     */
    onPressureTimer(callback) {
        this.pressureTimerCallbacks.add(callback);
        return () => this.pressureTimerCallbacks.delete(callback);
    }

    onContradictionDetected(callback) {
        this.contradictionCallbacks.add(callback);
        return () => this.contradictionCallbacks.delete(callback);
    }

    getLastContradiction() {
        return this._lastContradiction || null;
    }

    notifyContradictionListeners(data) {
        for (const callback of this.contradictionCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Interrogation] Error in contradiction callback:', error);
            }
        }
    }

    notifyTypingListeners(data) {
        for (const callback of this.typingCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Interrogation] Error in typing callback:', error);
            }
        }
    }

    notifySessionListeners(data) {
        for (const callback of this.sessionCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Interrogation] Error in session callback:', error);
            }
        }
    }

    notifyHesitationListeners(data) {
        for (const callback of this.hesitationCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Interrogation] Error in hesitation callback:', error);
            }
        }
    }

    notifyAnswerListeners(data) {
        for (const callback of this.answerCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Interrogation] Error in answer callback:', error);
            }
        }
    }

    notifySubmitAttemptListeners(data) {
        for (const callback of this.submitAttemptCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Interrogation] Error in submit attempt callback:', error);
            }
        }
    }

    notifyUpdateListeners() {
        const data = {
            questions: this.questions,
            sessions: this.sessions,
            answers: this.answers,
            categories: this.categories
        };
        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Interrogation] Error in update callback:', error);
            }
        }
    }

    notifyAutoAcceptTimerListeners(data) {
        for (const callback of this.autoAcceptTimerCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Interrogation] Error in auto-accept timer callback:', error);
            }
        }
    }

    notifyPressureTimerListeners(data) {
        for (const callback of this.pressureTimerCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Interrogation] Error in pressure timer callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getIsSessionActive() {
        return this.isSessionActive;
    }

    getSessionState() {
        return this.sessionState;
    }

    getLiveTyping() {
        return this.liveTyping;
    }

    getQuestionsList() {
        return this.questions;
    }

    getSessionsList() {
        return this.sessions;
    }

    getAnswersList() {
        return this.answers;
    }

    getCategoriesList() {
        return this.categories;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[Interrogation] Cleaning up...');
        this.isSessionActive = false;
        this.sessionState = null;
        this.liveTyping = null;
        this.questions = [];
        this.sessions = [];
        this.answers = [];
        this.categories = [];
        this._lastContradiction = null;
        this.typingCallbacks.clear();
        this.sessionCallbacks.clear();
        this.hesitationCallbacks.clear();
        this.answerCallbacks.clear();
        this.contradictionCallbacks.clear();
        this.updateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
